import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authService, getErrorMessage } from '../config/api';
import { jwtDecode } from 'jwt-decode';
import { authEvents, AUTH_EVENTS } from '../utils/authEvents';

const AuthContext = createContext();

// Buffer time before expiry to trigger logout (60 seconds)
const EXPIRY_BUFFER_MS = 60 * 1000;
// Warning time before expiry (5 minutes)
const EXPIRY_WARNING_MS = 5 * 60 * 1000;

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const expiryTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  /**
   * Clear any scheduled expiry timers
   */
  const clearExpiryTimers = useCallback(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  /**
   * Perform logout - clears state and storage
   */
  const performLogout = useCallback(() => {
    clearExpiryTimers();
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    setUser(null);
  }, [clearExpiryTimers]);

  /**
   * Schedule automatic logout based on token expiry
   */
  const scheduleTokenExpiry = useCallback((token) => {
    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) {
        console.warn('Token does not have exp claim, skipping expiry scheduling');
        return;
      }

      const expiresAtMs = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiresAtMs - now;

      // If token is already expired, logout immediately
      if (timeUntilExpiry <= 0) {
        console.log('Token already expired, logging out');
        performLogout();
        window.location.href = '/signin';
        return;
      }

      // Clear any existing timers
      clearExpiryTimers();

      // Schedule warning (5 minutes before expiry)
      const timeUntilWarning = timeUntilExpiry - EXPIRY_WARNING_MS;
      if (timeUntilWarning > 0) {
        warningTimerRef.current = setTimeout(() => {
          authEvents.emit(AUTH_EVENTS.TOKEN_EXPIRING_SOON, {
            expiresAt: expiresAtMs,
            remainingMs: EXPIRY_WARNING_MS
          });
          console.log('Token expiring soon (5 minutes remaining)');
        }, timeUntilWarning);
      }

      // Schedule logout (60 seconds before expiry for safety margin)
      const logoutTime = Math.max(timeUntilExpiry - EXPIRY_BUFFER_MS, 0);
      expiryTimerRef.current = setTimeout(() => {
        console.log('Token expired, logging out');
        authEvents.emit(AUTH_EVENTS.TOKEN_EXPIRED);
        performLogout();
        window.location.href = '/signin';
      }, logoutTime);

      console.log(`Token expiry scheduled: logout in ${Math.round(logoutTime / 1000 / 60)} minutes`);
    } catch (err) {
      console.error('Failed to schedule token expiry:', err);
    }
  }, [clearExpiryTimers, performLogout]);

  const setUserFromToken = useCallback((token) => {
    try {
      // Decode JWT to extract user information
      const decoded = jwtDecode(token);

      // JWT structure: { userId, email, sub: username, iat, exp, status }
      const userData = {
        userId: decoded.userId,
        name: decoded.sub, // username is in 'sub' (subject)
        email: decoded.email,
        status: decoded.status || 'ACTIVE' // Default to ACTIVE for backward compatibility
      };

      // Store email for persistence
      localStorage.setItem('userEmail', userData.email);

      // Set user state
      setUser(userData);

      // Schedule token expiry
      scheduleTokenExpiry(token);

      return userData;
    } catch (err) {
      console.error('Failed to decode token:', err);
      // Clear invalid token
      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      throw new Error('Invalid token');
    }
  }, [scheduleTokenExpiry]);

  // Subscribe to auth events (401 from API)
  useEffect(() => {
    const unsubscribe = authEvents.on(AUTH_EVENTS.UNAUTHORIZED, () => {
      console.log('Received unauthorized event, logging out');
      performLogout();
      // Redirect is handled by api.js interceptor
    });

    return () => unsubscribe();
  }, [performLogout]);

  useEffect(() => {
    // Check for existing auth token and get user profile on app load
    const checkAuthentication = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          setUserFromToken(token);
        }
      } catch (err) {
        console.error('Authentication check failed:', err);
        // Clear token if invalid
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    checkAuthentication();

    // Cleanup timers on unmount
    return () => clearExpiryTimers();
  }, [setUserFromToken, clearExpiryTimers]);

  const login = async (credentials) => {
    try {
      setError(null);
      const response = await authService.loginUser(credentials);
      const data = response.data;
      const token = data.token;
      // Store token
      localStorage.setItem('token', token);

      // Use token to set user state (ensures status is captured)
      const userData = setUserFromToken(token);
      return userData;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  };

  const signup = async (userData) => {
    try {
      setError(null);
      const response = await authService.signupUser(userData);
      const data = response.data;
      const token = data.token;
      // Store token
      localStorage.setItem('token', token);

      // Use token to set user state
      const userDataResult = setUserFromToken(token);
      return userDataResult;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      // await authService.logout(); // Optional: call backend logout if exists
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      // Use centralized logout which also clears expiry timers
      performLogout();
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    signOut,
    setUserFromToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}